import generatedContent from './content.generated.json' with { type: 'json' };
import type { ArgSchema } from './arg-schema.js';

export type MethodSafety = 'read' | 'write' | 'destructive';

export interface MethodDocs {
  description: string;
  params: Record<string, string>;
  /** Resolved field-level JSON Schema of the agent-facing arguments;
   *  its `x-arg-style` drives the client executor's positional-arg mapping. */
  argSchema: ArgSchema;
  returns: string;
  safety: MethodSafety;
  example?: string;
  related?: string[];
}

export interface ResourceDocs {
  description: string;
  methods: Record<string, MethodDocs>;
}

export interface DocSearchResult {
  name: string;
  resource: string;
  method: string;
  docs: MethodDocs;
  score: number;
}

type ContentShape = Record<string, {
  description: string;
  methods: Record<string, {
    description: string;
    params: Record<string, string>;
    argSchema: ArgSchema;
    returns: string;
    safety: MethodSafety;
    example?: string;
    related?: string[];
  }>;
}>;

export class DocsIndex {
  private resources: Map<string, ResourceDocs>;

  constructor() {
    this.resources = new Map();
    this.loadContent();
  }

  search(query: string): DocSearchResult[] {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) {
      return [];
    }

    const results: DocSearchResult[] = [];

    for (const [resourceName, resourceDocs] of this.resources) {
      for (const [methodName, methodDocs] of Object.entries(resourceDocs.methods)) {
        const score = this.scoreMatch(
          terms,
          resourceName,
          methodName,
          methodDocs,
        );
        if (score > 0) {
          results.push({
            name: `${resourceName}.${methodName}`,
            resource: resourceName,
            method: methodName,
            docs: methodDocs,
            score,
          });
        }
      }
    }

    results.sort((a, b) => b.score - a.score);
    return results;
  }

  resourceNames(): string[] {
    return [...this.resources.keys()].sort();
  }

  resource(name: string): ResourceDocs | undefined {
    return this.resources.get(name);
  }

  method(name: string): MethodDocs | undefined {
    const dotIdx = name.lastIndexOf('.');
    if (dotIdx === -1) {
      return undefined;
    }
    const resourceName = name.substring(0, dotIdx);
    const methodName = name.substring(dotIdx + 1);
    const resourceDocs = this.resources.get(resourceName);
    if (!resourceDocs) {
      return undefined;
    }
    return resourceDocs.methods[methodName];
  }

  private scoreMatch(
    terms: string[],
    resourceName: string,
    methodName: string,
    docs: MethodDocs,
  ): number {
    const fullName = `${resourceName}.${methodName}`.toLowerCase();
    const descLower = docs.description.toLowerCase();
    const paramText = Object.entries(docs.params)
      .map(([k, v]) => `${k} ${v}`)
      .join(' ')
      .toLowerCase();

    let score = 0;
    for (const term of terms) {
      if (methodName.toLowerCase() === term) {
        score += 10;
      } else if (methodName.toLowerCase().includes(term)) {
        score += 5;
      }

      if (fullName.includes(term)) {
        score += 3;
      }

      if (descLower.includes(term)) {
        score += 2;
      }

      if (paramText.includes(term)) {
        score += 1;
      }
    }

    return score;
  }

  private loadContent(): void {
    const content = generatedContent as unknown as ContentShape;
    for (const [resourceName, resourceData] of Object.entries(content)) {
      const methods: Record<string, MethodDocs> = {};
      for (const [methodName, methodData] of Object.entries(resourceData.methods)) {
        methods[methodName] = {
          description: methodData.description,
          params: methodData.params,
          argSchema: methodData.argSchema,
          returns: methodData.returns,
          safety: methodData.safety,
          example: methodData.example,
          related: methodData.related,
        };
      }
      this.resources.set(resourceName, {
        description: resourceData.description,
        methods,
      });
    }
  }
}
